/**
 * Vault Document → extracted text utility (for AI-context use).
 *
 * Takes a `VaultPickerResult` (the shape produced by `VaultFilePickerDialog`),
 * fetches the bytes via its signed download URL, and returns the
 * extracted text. Supports plain text (utf-8), .docx (mammoth), and
 * .pdf (pdfjs-dist).
 *
 * Plain async utility — NOT a React hook. Safe to call from event
 * handlers, callbacks, or other utilities.
 */

import type { VaultPickerResult } from '@/components/VaultFilePickerDialog';
import { SUPPORTED_EXTENSIONS } from '@/lib/hooks/useFileContext';
import { loadPdfJs } from '@fias/pdf-core/loader';

export interface ExtractedVaultDocumentText {
  name: string;
  content: string;
  mimeType?: string;
}

function getFileExtension(filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase();
  return ext ? `.${ext}` : '';
}

/**
 * Whether the picker result is something this utility knows how to
 * extract text from. Use as a `mimeFilter` for VaultFilePickerDialog so
 * the picker only shows extractable files.
 */
export function isVaultDocumentExtractable(mimeType: string | null, fileName: string): boolean {
  const ext = getFileExtension(fileName);
  if (SUPPORTED_EXTENSIONS.has(ext)) return true;
  if (!mimeType) return false;
  if (mimeType.startsWith('text/')) return true;
  if (mimeType === 'application/json') return true;
  if (mimeType === 'application/pdf') return true;
  if (mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
    return true;
  }
  return false;
}

async function extractTextFromDocx(arrayBuffer: ArrayBuffer): Promise<string> {
  const mammoth = await import('mammoth');
  const result = await mammoth.extractRawText({ arrayBuffer });
  return result.value.trim();
}

async function extractTextFromPdf(arrayBuffer: ArrayBuffer): Promise<string> {
  // pdf.js access goes through @fias/pdf-core's loader — the one place that
  // owns worker setup (a prior inline GlobalWorkerOptions copy here was the
  // canonical example of the library-discoverability gap; see docs/pdf-core.md).
  // loadPdfJs dynamic-imports pdfjs-dist internally, so the initial bundle
  // still doesn't carry it.
  const pdfjsLib = await loadPdfJs();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  const textParts: string[] = [];
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const textContent = await page.getTextContent();
    textParts.push(textContent.items.map((item) => ('str' in item ? item.str : '')).join(' '));
  }
  return textParts.join('\n\n').trim();
}

export class VaultDocumentExtractionError extends Error {
  constructor(
    message: string,
    public readonly cause?: unknown,
  ) {
    super(message);
    this.name = 'VaultDocumentExtractionError';
  }
}

export async function extractTextFromVaultDocument(
  picked: VaultPickerResult,
): Promise<ExtractedVaultDocumentText> {
  const response = await fetch(picked.downloadUrl);
  if (!response.ok) {
    throw new VaultDocumentExtractionError(
      `Failed to download "${picked.fileName}" (HTTP ${response.status})`,
    );
  }

  const ext = getFileExtension(picked.fileName);
  let content: string;
  if (ext === '.pdf' || picked.mimeType === 'application/pdf') {
    const arrayBuffer = await response.arrayBuffer();
    content = await extractTextFromPdf(arrayBuffer);
  } else if (
    ext === '.docx' ||
    picked.mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ) {
    const arrayBuffer = await response.arrayBuffer();
    content = await extractTextFromDocx(arrayBuffer);
  } else {
    content = await response.text();
  }

  const trimmed = content.trim();
  if (trimmed.length === 0) {
    throw new VaultDocumentExtractionError(
      `No text content could be extracted from "${picked.fileName}".`,
    );
  }

  return {
    name: picked.fileName,
    content: trimmed,
    mimeType: picked.mimeType ?? undefined,
  };
}
